import axios from "axios";
import { BACKEND_URL } from "../utils/envConfig";

const API_URL = `${BACKEND_URL}/subscription-history`;
const SUBSCRIPTION_TYPES_URL = `${BACKEND_URL}/subscription-types`;

/**
 * Get all subscription types (reference data)
 */
export const getSubscriptionTypes = async () => {
  try {
    const response = await axios.get(SUBSCRIPTION_TYPES_URL);
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription types:", error);
    throw error;
  }
};

export const updateSubscriptionHistoryOpdConsultationWaiver =
  async (
    subscriptionHistoryId,
    waiverData
  ) => {

    const enabled =
      waiverData?.enableOpdConsultationWaiver === true ||
      waiverData?.enableOpdConsultationWaiver === "true" ||
      waiverData?.enableOpdConsultationWaiver === 1 ||
      waiverData?.enableOpdConsultationWaiver === "1";

    let days = null;

    if (enabled) {
      days = Number(
        waiverData?.opdConsultationWaiverDays
      );

      if (!Number.isFinite(days) || days < 0) {
        throw new Error(
          "OPD consultation waiver days must be a valid non-negative number"
        );
      }

      days = Math.floor(days);
    }

    const payload = {
      enableOpdConsultationWaiver: enabled,
      opdConsultationWaiverDays: enabled
        ? days
        : null,
    };

    try {
      const response = await axios.put(
        `${API_URL}/${subscriptionHistoryId}/opd-consultation-waiver`,
        payload
      );

      return response.data;

    } catch (error) {

      if (error.response?.status === 404) {

        const response = await axios.put(
          `${API_URL}/${subscriptionHistoryId}`,
          payload
        );

        return response.data;
      }

      console.error(
        "Error updating OPD consultation waiver settings:",
        error
      );

      throw error;
    }
  };

/**
 * Create subscription onboarding record
 */
export const createSubscriptionOnboarding = async (onboardingData) => {
  try {
    const response = await axios.post(`${API_URL}/onboard`, onboardingData);
    return response.data;
  } catch (error) {
    console.error("Error creating subscription onboarding:", error);
    throw error;
  }
};

/**
 * Renew subscription
 */
export const renewSubscription = async (renewalData) => {
  try {
    const response = await axios.post(`${API_URL}/renew`, renewalData);
    return response.data;
  } catch (error) {
    console.error("Error renewing subscription:", error);
    throw error;
  }
};

/**
 * Upgrade or downgrade subscription
 */
export const changeSubscription = async (changeData) => {
  try {
    const response = await axios.post(`${API_URL}/change`, changeData);
    return response.data;
  } catch (error) {
    console.error("Error changing subscription:", error);
    throw error;
  }
};

/**
 * Get subscription history for an organization
 */
export const getSubscriptionHistory = async (organizationId) => {
  try {
    const response = await axios.get(`${API_URL}/organization/${organizationId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription history:", error);
    throw error;
  }
};

/**
 * Get active subscription for an organization
 */
export const getActiveSubscription = async (organizationId) => {
  try {
    const response = await axios.get(`${API_URL}/organization/${organizationId}/active`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No active subscription
    }
    console.error("Error fetching active subscription:", error);
    throw error;
  }
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (organizationId, notes = "") => {
  try {
    const response = await axios.post(
      `${API_URL}/cancel/${organizationId}`,
      { notes }
    );
    return response.data;
  } catch (error) {
    console.error("Error cancelling subscription:", error);
    throw error;
  }
};

/**
 * Update subscription history status
 */
export const updateSubscriptionHistoryStatus = async (subscriptionHistoryId, status) => {
  try {
    const response = await axios.put(
      `${API_URL}/${subscriptionHistoryId}/status`,
      { status }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating subscription history status:", error);
    throw error;
  }
};

/**
 * Update subscription history with payer and recipient details (for cash payments)
 */
/**
 * Update GST settings on a subscription history record (typically active subscription)
 */
export const updateSubscriptionHistoryGst = async (subscriptionHistoryId, gstData) => {
  const payload = {
    includeGST: Boolean(gstData.includeGST),
    gstPercentage:
      gstData.includeGST && gstData.gstPercentage != null && gstData.gstPercentage !== ''
        ? parseFloat(gstData.gstPercentage)
        : null,
  };

  try {
    const response = await axios.put(`${API_URL}/${subscriptionHistoryId}/gst`, payload);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      const response = await axios.put(`${API_URL}/${subscriptionHistoryId}`, payload);
      return response.data;
    }
    console.error('Error updating subscription history GST:', error);
    throw error;
  }
};

/**
 * Build cash payment details payload (payer/recipient + optional comment/proof).
 * Proof may be a data-URL (`data:<mime>;base64,...`) or raw base64.
 */
export const buildCashPaymentDetailsPayload = (cashDetails = {}) => {
  const proofData = cashDetails.paymentProofData || cashDetails.paymentProofBase64 || null;
  let paymentProofContentType =
    cashDetails.paymentProofContentType || null;
  let paymentProofBase64 = proofData;

  if (typeof proofData === "string" && proofData.startsWith("data:")) {
    const match = proofData.match(/^data:([^;]+);base64,([\s\S]+)$/);
    if (match) {
      paymentProofContentType = match[1];
      paymentProofBase64 = match[2];
    }
  }

  return {
    payerName: cashDetails.payerName || null,
    payerMobile: cashDetails.payerMobile || null,
    recipientName: cashDetails.recipientName || null,
    recipientMobile: cashDetails.recipientMobile || null,
    paymentComment: cashDetails.paymentComment || null,
    paymentProofFileName:
      cashDetails.paymentProofFileName || cashDetails.paymentProofName || null,
    paymentProofContentType,
    paymentProofBase64: paymentProofBase64 || null,
  };
};

/**
 * Update subscription history with payer/recipient details and optional
 * payment comment + proof (image/PDF) for cash / split payments.
 */
export const updateSubscriptionHistoryCashDetails = async (subscriptionHistoryId, cashDetails) => {
  try {
    const payload = buildCashPaymentDetailsPayload(cashDetails);
    const response = await axios.put(
      `${API_URL}/${subscriptionHistoryId}/cash-details`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating subscription history cash details:", error);
    throw error;
  }
};

export default {
  getSubscriptionTypes,
  createSubscriptionOnboarding,
  renewSubscription,
  changeSubscription,
  getSubscriptionHistory,
  getActiveSubscription,
  cancelSubscription,
  updateSubscriptionHistoryStatus,
  updateSubscriptionHistoryGst,
  updateSubscriptionHistoryCashDetails,
  buildCashPaymentDetailsPayload,
};

